import React, { useState, useEffect } from 'react';
import { Upload, X, ImageIcon, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { logoService } from '../services/api';
import { useToast } from '../hooks/use-toast';

const LogoUpload = () => {
  const [logo, setLogo] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [previewUrl, setPreviewUrl] = useState(null);
  const { toast } = useToast();

  // Load current logo on component mount
  useEffect(() => {
    loadCurrentLogo();
  }, []);

  const loadCurrentLogo = async () => {
    try {
      setLoading(true);
      const response = await logoService.get();
      if (response.logo_url) {
        setLogo(response.logo_url);
      }
    } catch (error) {
      console.error('Error loading logo:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast({
          variant: "destructive",
          title: "Erreur",
          description: "Veuillez sélectionner un fichier image (PNG, JPG, JPEG)"
        });
        return;
      }

      // Validate file size (2MB max)
      if (file.size > 2 * 1024 * 1024) {
        toast({
          variant: "destructive",
          title: "Erreur",
          description: "La taille du fichier ne doit pas dépasser 2MB"
        });
        return;
      }

      // Create preview URL
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);

      // Upload file
      uploadLogo(file);
    }
  };

  const uploadLogo = async (file) => {
    try {
      setUploading(true);
      const response = await logoService.upload(file);
      
      setLogo(response.logo_url);
      setPreviewUrl(null); // Clear preview since we now have the uploaded logo
      
      toast({
        title: "Succès",
        description: "Logo uploadé avec succès"
      });
    } catch (error) {
      console.error('Error uploading logo:', error);
      setPreviewUrl(null);
      
      const errorMessage = error.response?.data?.detail || "Erreur lors de l'upload du logo";
      toast({
        variant: "destructive",
        title: "Erreur",
        description: errorMessage
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteLogo = async () => {
    try {
      setUploading(true);
      await logoService.delete();
      
      setLogo(null);
      setPreviewUrl(null);
      
      toast({
        title: "Succès",
        description: "Logo supprimé avec succès"
      });
    } catch (error) {
      console.error('Error deleting logo:', error);
      
      const errorMessage = error.response?.data?.detail || "Erreur lors de la suppression du logo";
      toast({
        variant: "destructive",
        title: "Erreur",
        description: errorMessage
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Logo Preview - Compact */}
      <div className="flex justify-center">
        <div className="w-24 h-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50">
          {loading ? (
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          ) : logo || previewUrl ? (
            <img
              src={previewUrl || logo}
              alt="Logo du club"
              className="w-full h-full object-cover rounded-lg"
            />
          ) : (
            <div className="text-center">
              <ImageIcon className="w-8 h-8 text-gray-400 mx-auto mb-1" />
              <span className="text-xs text-gray-500">Aucun logo</span>
            </div>
          )}
        </div>
      </div>

      {/* Upload Button */}
      <div className="flex gap-2 justify-center">
        <div className="flex-1 max-w-xs">
          <input
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
            id="logo-upload"
            disabled={uploading}
          />
          <label htmlFor="logo-upload">
            <Button 
              variant="outline" 
              size="sm"
              className="w-full cursor-pointer" 
              disabled={uploading}
              asChild
            >
              <span>
                {uploading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4 mr-2" />
                )}
                {logo ? 'Changer' : 'Ajouter'}
              </span>
            </Button>
          </label>
        </div>

        {/* Delete Button */}
        {logo && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleDeleteLogo}
            disabled={uploading}
            className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
          >
            <X className="w-4 h-4" />
          </Button>
        )}
      </div>

      {/* Info - Compact */}
      <div className="text-xs text-gray-500 text-center space-y-1 bg-gray-50 p-2 rounded">
        <p>PNG, JPG • Max 2MB • 200x200px recommandé</p>
      </div>
    </div>
  );
};

export default LogoUpload;